import { useCallback, useEffect, useState } from 'react'
import { navigateInParent } from '@/lib/app-messaging'

interface UseParentNavigationReturn {
  /** Navigate to a route in the parent application */
  navigate: (route: string, options?: { replaceUrl?: boolean; openInNewTab?: boolean; isInternalRoute?: boolean }) => void
  /** Whether the app should render navigation links (based on iframe context) */
  shouldRenderNavigationLinks: boolean
}

/**
 * Hook for handling navigation to parent application when running in an iframe.
 * Uses the same logic as sendMessageToParent to detect iframe context.
 */
export function useParentNavigation(): UseParentNavigationReturn {
  const [hasParent, setHasParent] = useState(false)

  useEffect(() => {
    // Use the same logic as sendMessageToParent
    const checkParentAvailable = () => {
      setHasParent(typeof window !== "undefined" && window.parent && window.parent !== window)
    }

    checkParentAvailable()
    
    // Re-check on window focus in case context changes
    window.addEventListener('focus', checkParentAvailable)
    
    return () => {
      window.removeEventListener('focus', checkParentAvailable)
    }
  }, [])

  const navigate = useCallback((route: string, options: { replaceUrl?: boolean; openInNewTab?: boolean; isInternalRoute?: boolean } = {}) => {
    navigateInParent(route, options)
  }, [])

  return {
    navigate,
    shouldRenderNavigationLinks: hasParent,
  }
} 